import React, { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { SwatchStack } from './legendGlyphs';

/**
 * Wraps a legend that has no collapse of its own so it starts hidden.
 *
 * The three keys built as pills (Region status, Mode & status, the combined
 * Legend) already open only when asked. The hazard pills and the weather scale
 * were rendered unconditionally instead, so those two were the only keys
 * covering the map on arrival. Rather than give each its own toggle — a third
 * and fourth copy of the same button — they share this one.
 *
 * The panel sits ABOVE the toggle and the toggle is last in DOM order, so the
 * pill never moves as the panel opens: the legend grows away from the map's
 * bottom edge rather than through it. That is the same arrangement the other
 * three use, and the reason they anchor by `bottom`.
 *
 * `swatches` is optional: SwatchStack carries its own `ml-auto`, so when there
 * are none the chevron takes over pushing itself to the right edge.
 */
const LegendCollapse = ({
  label,
  swatches = null,
  align = 'start',
  defaultOpen = false,
  className = '',
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasSwatches = Array.isArray(swatches) && swatches.length > 0;

  return (
    <div className={`flex flex-col gap-[4px] ${align === 'end' ? 'items-end' : 'items-start'} ${className}`}>
      {open && children}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex h-[28px] w-fit items-center rounded-[14px] border border-solid border-[#E6E9E4] bg-white pl-[10px] pr-[8px] drop-shadow-[0px_1px_1.5px_rgba(17,24,39,0.07)] dark:border-[#2A302C] dark:bg-[#1B1F1C]"
      >
        <span className="font-['Inter'] text-[10px] font-semibold whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
          {label}
        </span>
        {hasSwatches && <SwatchStack colors={swatches} />}
        <ChevronUp
          size={10}
          className={`${hasSwatches ? '' : 'ml-[6px]'} shrink-0 text-[#5D625F] transition-transform dark:text-[#9AA09A] ${open ? '' : 'rotate-180'}`}
        />
      </button>
    </div>
  );
};

export default LegendCollapse;
